import { useEffect, useRef, useState, type FormEvent, type KeyboardEvent, type ReactNode } from 'react'
import { api, ApiError, can, type BookSave, type CallQuestion, type ListItem, type Member } from '../lib/api'
import { findNatures } from '../lib/natures'
import {
  QuestionFields, alerting, answerText, callCodes, mergeTrees, outstanding, picked, prune,
  underStaying, visible,
} from '../lib/questions'
import { decodeVehicle, vehicleText } from '../lib/vehicles'
import { PhoneInput } from './PhoneInput'
import { PinMap } from './PinMap'
import { mapSettings } from '../lib/basemap'
import { AudiencePicker, reachesNobody, useAudiences } from './AudiencePicker'
import { CallerPastCalls } from './CallerHistory'
import { BookChoice } from './CallParts'
import { NotePair } from './CallNotes'
import {
  PastCallFields, emptyPast, pastBody, pastLine, pastMissing, pastTimesMissing, type PastLog,
} from './LoggedCall'

export const PRIORITIES = [
  { v: 1, label: 'Critical' },
  { v: 2, label: 'Urgent' },
  { v: 3, label: 'Normal' },
  { v: 4, label: 'Low' },
  { v: 5, label: 'Routine' },
]

/** What a safety answer raises the call to, said in the word the chips use.
 *
 *  `call_questions.ALERT_PRIORITY` is 1 and 1 is Critical on this scale. Read
 *  off the list above rather than written out, so renaming a chip cannot leave
 *  the sentence naming an urgency that no longer exists on screen. */
const ALERT_LABEL = PRIORITIES.find((p) => p.v === 1)!.label

/** The four steps of Take a call (style B). */
type Step = 1 | 2 | 3 | 4
const STEP_NAMES = ['Caller', 'Where', 'What', 'Send']

function newSession(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `s-${Date.now()}-${Math.random().toString(36).slice(2)}`
}

export type IntakePrefill = {
  caller_name: string
  caller_phone: string
  caller_address?: string | null
  contact_id?: string | null
  /** THE NAME THE PHONE SYSTEM ITSELF GAVE (079 review), apart from the
   *  phone book's, which the card shows first: only this one is "how the
   *  call came in" and kept for good. */
  pbx_name?: string | null
}

/** THE FORM AS A DRAFT (069): everything a dispatcher has typed or pressed,
 *  and nothing the form works out for itself (suggestions, the preview, the
 *  question tree -- fetched again). The screen's own shape, saved and handed
 *  back by the server unread; `v` is for the day it changes. */
export type IntakeDraft = {
  v: 1
  prefill: IntakePrefill | null
  form?: Record<string, unknown>
  urgency?: number | null
  callerUrgency?: number | null
  said?: string
  spot?: { label: string; lat: number; lon: number; moved: boolean; wide?: boolean } | null
  /** "I-87 northbound, mile marker 23" (071). */
  locDetails?: string
  cross?: { a: string; b: string }
  anon?: boolean
  locNote?: string
  /** THE TWO NOTES (081): internal, never sent; and for the responder, sent
   *  to whoever takes the call. As typed, line breaks and all. */
  noteInternal?: string
  noteResponder?: string
  /** `save` and `name`: where each goes in the phone book (25 Oct 2026). */
  others?: { label: string; phone: string; save?: BookSave; name?: string }[]
  area?: string | null
  audience?: string[]
  answers?: Record<string, string>
  autoFilled?: boolean
  crossFilled?: { label: string; a: string; b: string } | null
  /** THE OTHER NATURES (076), after the first, in order: "a boost, and the
   *  car is also locked". */
  extraNatures?: { id: string; name: string }[]
  /** A CALL THAT ALREADY HAPPENED (078): when it came in, who went, how it
   *  ended -- and Save, nothing sent. `on` is the mode. */
  past?: PastLog
}

/** The form's own boxes out of a saved draft: the defaults, then the
 *  caller it was started for, then what was typed -- as the form reads them
 *  when it opens. */
function draftForm(d: IntakeDraft) {
  const f = {
    nature_id: '', nature_label: '', location_label: '',
    caller_name: '', caller_phone: '',
    relationship_id: '', relationship_label: '',
    caller_on_scene: true as boolean | null,
    ...(d.prefill ?? {}),
    ...((d.form ?? {}) as object),
  } as Record<string, unknown>
  const s = (k: string) => (typeof f[k] === 'string' ? (f[k] as string) : '')
  return { f, s }
}

/** WHAT OPENS THE CALL (POST /api/incidents), from the form as saved -- THE
 *  ONE DEFINITION, used by "Create and broadcast" here and by "Broadcast
 *  drafts" (074) for a draft nobody has open, so a draft opens the same call
 *  whichever button opened it. Only a pin the dispatcher MOVED travels (053),
 *  and only while it is still for the address or the details in the box. */
export function openBody(d: IntakeDraft, draftId: string | null): Record<string, unknown> {
  const { f, s } = draftForm(d)
  const details = (d.locDetails ?? '').trim()
  const label = s('location_label').trim()
  const spot = d.spot && d.spot.label
    && (d.spot.label === label || d.spot.label === details) ? d.spot : null
  /* AND THE ONE THE CALL CARRIES, which is a narrower thing: only a pin the
     dispatcher MOVED himself. The first build sent whatever was on screen, so
     every call to an address the map could place filed the geocoder's own
     answer as a named person's claim about where to go -- "the loading bay
     behind it", attributed to a dispatcher who never touched the map.
     `incident_locations` is where a human's correction lives and 053's own
     column comment says a geocoder never writes it; this is the line that
     makes that true. */
  const carried = spot?.moved ? spot : null
  const cross = d.cross ?? { a: '', b: '' }
  return {
    nature_id: s('nature_id') || null,
    nature_label: s('nature_label'),
    location_label: s('location_label'),
    location_details: details || null,
    caller_name: s('caller_name') || null,
    caller_phone: s('caller_phone') || null,
    anonymous: d.anon ?? false,
    other_numbers: (d.others ?? [])
      .filter((o) => o.phone.trim())
      .map((o) => ({
        label: o.label.trim() || null, phone: o.phone.trim(),
        // Where it goes in the phone book (25 Oct 2026). A row from an older
        // draft says nothing, which is nowhere -- as it always was.
        save: o.save ?? 'no', name: (o.name ?? '').trim() || null,
      })),
    relationship_id: s('relationship_id') || null,
    relationship_label: s('relationship_label') || null,
    caller_on_scene: f.caller_on_scene as boolean | null,
    // OMITTED when he never touched the control — see `urgency`. Sending
    // null instead would rest on how one pydantic version treats a
    // constrained optional; leaving the key out cannot be read two ways.
    priority: d.urgency ?? undefined,
    caller_urgency: d.callerUrgency ?? null,
    // ONLY A PIN HE MOVED HIMSELF -- see `carried`. The geocoder's own
    // answer is written onto the address in the background, where it
    // belongs; sending it here would record a guess as a dispatcher's
    // claim about where to go.
    latitude: carried?.lat,
    longitude: carried?.lon,
    cross_street_a: cross.a.trim() || null,
    cross_street_b: cross.b.trim() || null,
    location_note: (d.locNote ?? '').trim() || null,
    // AS TYPED, line breaks and all (081); the server trims the ends.
    internal_note: (d.noteInternal ?? '').trim() ? d.noteInternal : null,
    responder_note: (d.noteResponder ?? '').trim() ? d.noteResponder : null,
    broadcast_area: d.area ?? null,
    answers: d.answers ?? {},
    extra_natures: (d.extraNatures ?? []).map((n) => n.id),
    draft_id: draftId,
    // TAKEN FROM THE RINGING CALLER-ID CARD UNCHANGED (079): kept as it came
    // in, never corrected. The server checks the number against its call log.
    from_phone: d.prefill ? {
      number: !!d.prefill.caller_phone && s('caller_phone') === d.prefill.caller_phone,
      name: !!d.prefill.pbx_name && s('caller_name') === d.prefill.pbx_name,
    } : null,
  }
}

/** What a saved draft's own broadcast preview is asked with (074): the same
 *  boxes the form sends `/preview-broadcast`. The server applies the raise a
 *  red answer makes when no urgency was pressed. */
export function previewBody(d: IntakeDraft): Record<string, unknown> {
  const { f, s } = draftForm(d)
  const cross = d.cross ?? { a: '', b: '' }
  return {
    nature_id: s('nature_id') || null,
    nature_label: s('nature_label'),
    location_label: s('location_label'),
    location_details: (d.locDetails ?? '').trim() || null,
    cross_street_a: cross.a.trim() || null,
    cross_street_b: cross.b.trim() || null,
    broadcast_area: d.area ?? null,
    priority: d.urgency ?? undefined,
    answers: d.answers ?? {},
    extra_natures: (d.extraNatures ?? []).map((n) => n.id),
    // Not staying asks what is asked only then (077).
    caller_on_scene: f.caller_on_scene as boolean | null,
  }
}

/** Why a saved draft cannot be opened as it stands, or null when it can --
 *  the two things the server refuses, and the form's own button with them. */
export function draftMissing(d: IntakeDraft): string | null {
  // A CALL THAT ALREADY HAPPENED IS NEVER BROADCAST (078): it is saved from
  // its own form, and a press of "Broadcast drafts" must not send it out.
  if (d.past?.on) return 'A call that already happened — open it to save it'
  const { s } = draftForm(d)
  if (!s('nature_label').trim()) return 'Missing the nature of call — open it to finish'
  if (!s('location_label').trim() && !(d.locDetails ?? '').trim()) {
    return 'Missing the address — open it to finish'
  }
  return null
}

/** Stages 1-3: the call arrives, intake happens, the record is marked. */
export function Intake({
  natures, relationships, prefill: givenPrefill, draft, draftId, saved,
  onSnapshot, onBusy, onDone, onCancel, onError, onSaveDraft,
  roster = [], resolutions = [],
}: {
  natures: ListItem[]
  relationships: ListItem[]
  /** The roster and the outcomes, for a call that already happened (078):
   *  who went, and how it ended. */
  roster?: Member[]
  resolutions?: ListItem[]
  /** THE DRAFT THIS FORM STARTS FROM (069), as it was last saved. Every box
   *  and chip comes back exactly as it was left; its prefill is the caller
   *  it was started for. */
  draft?: IntakeDraft | null
  /** Which draft this is, so opening the call marks it -- once. */
  draftId?: string | null
  /** "Saved as a draft 14:02", drawn in the heading. */
  saved?: ReactNode
  /** Called with the whole form after every change, to be saved. */
  onSnapshot?: (d: IntakeDraft) => void
  /** True while the call is being opened or the caller joined. */
  onBusy?: (busy: boolean) => void
  /** From the caller-ID pop, so the dispatcher is not retyping what the PBX
   *  already told us while somebody is on the line. `caller_address` is the
   *  address on the CALLER'S record — not the incident's, until a relationship
   *  says the two are the same. */
  prefill?: {
    caller_name: string
    caller_phone: string
    caller_address?: string | null
    contact_id?: string | null
  } | null
  /** `warning` is anything that did not stick — answers the server dropped, or
   *  a refusal it recorded — and it arrives AFTER the id on purpose. The call
   *  itself was opened either way; nothing about the questions may stop that,
   *  so this is never thrown and never blocks the hand-off to the working
   *  screen. */
  onDone: (id: string, warning?: string | null,
           /** A call that already happened was SAVED (078): closed, it is under
            *  Past calls rather than on the board. */
           logged?: { closed: boolean; number: string }) => void
  onCancel: () => void
  onError: (m: string) => void
  /** "Save as draft" (074, his Q2 A): saved, the form closed, nothing sent. */
  onSaveDraft?: () => void
}) {
  const prefill = draft ? draft.prefill : givenPrefill
  const [form, setForm] = useState({
    nature_id: '', nature_label: '', location_label: '',
    caller_name: '', caller_phone: '',
    relationship_id: '', relationship_label: '',
    caller_on_scene: true,
    ...(prefill ?? {}),
    ...((draft?.form ?? {}) as object),
    // Never carried into the form itself: the caller's address is a fact about
    // HIM, and the incident's location is a different fact that happens to
    // coincide sometimes.
    caller_address: undefined, contact_id: undefined,
  })

  /** NULL UNTIL HE PRESSES ONE, and that is a contract with the server rather
   *  than a nicety.
   *
   *  A safety answer — "is a child locked inside?" answered yes — takes the
   *  call to the top of the queue, and it must not do that over an urgency a
   *  dispatcher set deliberately. With a default of 3 there is no way to tell
   *  "he pressed Normal" from "he never looked at the control", so the raise
   *  would either always fire or never fire. So this stays null and the key is
   *  left out of the request entirely; the server still defaults to 3.
   */
  const [urgency, setUrgency] = useState<number | null>(draft?.urgency ?? null)
  /** How urgently the CALLER needs help (item 7, 23 Sep 2026): "Incident
   *  Priority = Normal, Caller Urgency = Urgent. These are two completely
   *  independent values." The same five words, a second row of chips. */
  const [callerUrgency, setCallerUrgency] = useState<number | null>(draft?.callerUrgency ?? null)
  /** Open calls this caller may be ringing about (item 5, 23 Sep 2026), and
   *  the words he adds if he is joined to one. Asked of the server as the
   *  nature or the address is typed; the strip only OFFERS. */
  const [similar, setSimilar] = useState<Awaited<ReturnType<typeof api.incidentSimilar>>>([])
  const [said, setSaid] = useState(draft?.said ?? '')
  const [joining, setJoining] = useState<string | null>(null)

  /** WHERE, IN WORDS THAT ARE NOT AN ADDRESS (071, the customer: "an
   *  intersection ... the 87 northbound or southbound, mile marker"). Kept as
   *  typed, looked up on the map as a try, and enough on its own to open the
   *  call: nothing about the map may stop it. */
  const [details, setDetails] = useState(draft?.locDetails ?? '')
  /** The details in the box NOW, for a lookup answering late -- `labelNow`'s
   *  twin. */
  const detailsNow = useRef('')
  detailsNow.current = details
  /** The details text the map last ANSWERED about -- `answered`'s twin. */
  const detailsAnswered = useRef('')

  useEffect(() => {
    const where = form.location_label.trim() || details.trim()
    const what = form.nature_label.trim()
    if (where.length < 4 && what.length < 3 && !form.nature_id) { setSimilar([]); return }
    let alive = true
    const t = setTimeout(() => {
      api.incidentSimilar({ location_label: where, nature_label: what, nature_id: form.nature_id || null })
        .then((rows) => { if (alive) setSimilar(rows) })
        .catch(() => { if (alive) setSimilar([]) })
    }, 350)
    return () => { alive = false; clearTimeout(t) }
  }, [form.location_label, form.nature_label, form.nature_id, details])

  /** JOIN THIS CALLER TO AN OPEN CALL. Everything typed goes with him --
   *  name, number, relationship, what he said, his answers -- as a report on
   *  that call. No call is opened and nothing is sent. */
  async function joinCall(id: string) {
    if (joining) return
    setJoining(id)
    onBusy?.(true)
    try {
      const added = await api.incidentAddReport(id, {
        caller_name: form.caller_name || null,
        caller_phone: form.caller_phone || null,
        relationship_label: form.relationship_label || null,
        said: said.trim() || null,
        answers: Object.keys(answers).length ? answers : null,
        // THE MARK TRAVELS WITH HIM (061): kept, never given out.
        anonymous: anon,
        draft_id: draftId ?? null,
        // THE NOTES GO WITH HIM TOO (081), added to that call's notes.
        internal_note: notes.internal.trim() ? notes.internal : null,
        responder_note: notes.responder.trim() ? notes.responder : null,
      })
      // A NOTE THAT COULD NOT BE STORED IS SAID (081 review).
      onDone(id, added.notes?.note ?? null)
    } catch (e) {
      onError(e instanceof ApiError ? e.message : 'Could not add this caller to that call.')
    } finally {
      setJoining(null)
      onBusy?.(false)
    }
  }
  const [busy, setBusy] = useState(false)

  /** THE NATURE LIST IS SHUT UNTIL HE ASKS. The customer, looking at five rows
   *  standing open on an empty form: "why do I need four open all the time
   *  even before I start typing". Typing opens it and narrows it; the down
   *  arrow opens it whole, for somebody who would rather browse seventy
   *  natures than remember one. */
  const [browsing, setBrowsing] = useState(false)
  /** Whether this role may broadcast at all. Read ONCE, so the button's label
   *  and what the button does cannot drift: a role without it used to press
   *  "Create and broadcast" and have nothing sent, with nothing on screen
   *  saying so. */
  const mayBroadcast = can('calls.active.actions.broadcast')
  /** Whether the other numbers may go into the phone book (25 Oct 2026):
   *  the phone book's own Add tick, which the server checks too. */
  const mayBook = can('phonebook.add')

  /** A CALL THAT ALREADY HAPPENED (078, the customer: "dispatching with pen
   *  and paper ... we just want to be able to log it"): its own tick, its own
   *  step 4, and Save instead of Create and broadcast -- nothing is sent. */
  const mayLog = can('calls.past.log_afterwards')
  const [past, setPast] = useState<PastLog>(() => ({ ...emptyPast(), ...(draft?.past ?? {}) }))
  /** A draft saved as one by somebody who may log it, opened by somebody who
   *  may not: it is never offered to Create and broadcast. */
  const pastLocked = past.on && !mayLog
  const logging = past.on && mayLog
  /** "The time it came in is not filled in. Save it anyway?" -- open while it
   *  asks; any change to the times answers it again. */
  const [timesWarn, setTimesWarn] = useState(false)
  useEffect(() => { setTimesWarn(false) }, [past.time, past.closedAt, past.open, past.date])

  /** Addresses this organization has already been to, offered as he types.
   *  They cost no map request at all -- the street a service has been called
   *  to forty times is the street it will be called to again. */
  const [offered, setOffered] = useState<
    Awaited<ReturnType<typeof api.placeSuggest>>['suggestions']
  >([])

  /** WHERE THE MAP SAYS THIS ADDRESS IS, and whether a human has since moved
   *  it. `label` is what the coordinates are FOR: the moment the box holds
   *  anything else, this is about a different address and is not drawn.
   *
   *  `moved` is the load-bearing half. Only a pin the DISPATCHER dragged is
   *  sent with the call -- storing the geocoder's own answer as "the pin"
   *  would file a guess as a person's claim about where to go, which is the
   *  one thing this product's map rules forbid. */
  const [spot, setSpot] = useState<
    { label: string; lat: number; lon: number; moved: boolean; wide?: boolean } | null
  >(draft?.spot ?? null)
  /** What the map said when it could not place the address. Shown as a
   *  sentence, because "ask for a cross street" is an action. */
  const [mapSaid, setMapSaid] = useState<string | null>(null)
  const [looking, setLooking] = useState(false)
  /** The last address the map has ANSWERED about, so leaving the box twice
   *  does not spend two of the geocoder's requests on one address -- and so
   *  that editing an address and putting it back does not leave the form with
   *  no map and nothing to say. It used to be stamped before the request, so
   *  a lookup that failed, or one whose text was restored, could never be
   *  asked again. */
  const answered = useRef(draft?.spot?.label ?? '')
  /** The address in the box NOW -- not in the render a lookup was started from.
   *  A pick sets the box and asks the map in one handler, so the handler's own
   *  `form` still holds the half-typed text, and the answer used to be thrown
   *  away as stale: at the Google search boost no picked address was ever
   *  pinned. Written on every render and by a pick itself. */
  const labelNow = useRef('')
  labelNow.current = form.location_label
  /** The address a request is in flight for, which is the other half: two
   *  lookups for one address would otherwise both be sent while neither had
   *  answered. */
  const inFlight = useRef('')
  /** An address that is settled -- picked from the list, or looked up -- so
   *  the suggestion list does not reopen on top of the answer. */
  const settled = useRef(draft?.spot?.label ?? '')
  /** THE MAP PROVIDER'S BILLING SESSION. Google bills every keystroke of one
   *  address plus the call that resolves the chosen suggestion as ONE charge
   *  when they share a token — and as one charge per keystroke when they do
   *  not. Minted here, sent with each suggestion request, and replaced the
   *  moment a suggestion is resolved, because that is what ends a session.
   *  `randomUUID` needs a secure context, which this application is; the
   *  fallback is for a browser that lacks it, where a duplicate token costs
   *  money and nothing else. */
  const session = useRef(newSession())
  /** What the map said when it could not offer suggestions — shown so the
   *  dispatcher knows the list is his own addresses only. */
  const [suggestNote, setSuggestNote] = useState<string | null>(null)

  /** "BETWEEN ___ AND ___" -- the two roads either side. With both given the
   *  broadcast names the street and these two instead of the house (the
   *  customer's rule), and the address remembers them for the next call. */
  const [cross, setCross] = useState(draft?.cross ?? { a: '', b: '' })
  /** The caller asked not to be identified (item 4, 23 Sep 2026). A MARK:
   *  everything typed is kept as for any call -- the customer: "never
   *  erasing information" -- and the mark tells every dispatcher it goes to
   *  nobody; the server keeps it out of every message. */
  const [anon, setAnon] = useState(draft?.anon ?? false)
  /** "Back entrance, ring twice" -- about finding this place on this call
   *  (060). Goes with the call, not with the address book. */
  const [locNote, setLocNote] = useState(draft?.locNote ?? '')
  /** THE TWO NOTES (081), ONE TEXT EACH, drawn in step 1 and again in step 3:
   *  whatever was typed in one place is there in the other. */
  const [notes, setNotes] = useState({
    internal: draft?.noteInternal ?? '', responder: draft?.noteResponder ?? '',
  })
  const setNote = (kind: 'internal' | 'responder', text: string) =>
    setNotes((n) => ({ ...n, [kind]: text }))
  /** OTHER NUMBERS TO REACH THE CALLER ON -- "my battery is low, call me at
   *  home", "call my wife" (055). On this call only, never in the broadcast. */
  const [others, setOthers] = useState<NonNullable<IntakeDraft['others']>>(draft?.others ?? [])
  /** The pair a PICKED address filled in, and which address it was for. While
   *  the boxes still hold exactly that pair it is ours rather than his, and it
   *  must not survive a change of address: "Y Street, between X's cross
   *  streets" would go out, and would be remembered on Y for every call after. */
  const crossFilled = useRef<{ label: string; a: string; b: string } | null>(draft?.crossFilled ?? null)
  const crossIsOurs = (c: { a: string; b: string }) =>
    !!crossFilled.current && c.a === crossFilled.current.a && c.b === crossFilled.current.b
  useEffect(() => {
    const filled = crossFilled.current
    if (filled && form.location_label.trim() !== filled.label && crossIsOurs(cross)) {
      crossFilled.current = null
      setCross({ a: '', b: '' })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.location_label])
  /** The area the broadcast names INSTEAD of the street -- "Flat Tire — TOSH".
   *  A label on the message, and nothing to do with who is sent it: "not the
   *  group of members receiving the message should define if the address
   *  should be written out". One at a time; pressing it again takes it off. */
  const [area, setArea] = useState<string | null>(draft?.area ?? null)
  /** Who it is broadcast to. Empty is everyone on duty, where every call starts. */
  const [audience, setAudience] = useState<string[]>(draft?.audience ?? [])
  const audiences = useAudiences()
  /** The area buttons are the organization's area names -- the same names its
   *  members are grouped under, used here only as words. */
  const areas = (audiences?.groups ?? []).filter((g) => g.kind === 'area')
  /** The exact text this call would send, from the server's own composer. */
  const [willGet, setWillGet] = useState<
    Awaited<ReturnType<typeof api.incidentPreviewBroadcast>> | null
  >(null)

  /** The caller's address, and it stops being offered the moment the person on
   *  the form stops being the person it belongs to.
   *
   *  This is the half that has no tell. Clearing the prefill on exit stops the
   *  form OPENING with a stranger's details -- but a dispatcher who does the
   *  obviously right thing and types over the name and callback number with
   *  whoever is actually on the phone would still have been offered the
   *  previous caller's address, and picking "Self" would have written it in
   *  under a footnote reading "Filled in from <the new name>'s record". An
   *  address attributed by name to somebody it does not belong to, with a
   *  sentence explaining why it is correct.
   *
   *  So the address is tied to the identity it arrived with. Edit either half
   *  of that identity and it is gone. */
  const identityIntact =
    !!prefill &&
    form.caller_name === (prefill.caller_name ?? '') &&
    form.caller_phone === (prefill.caller_phone ?? '')

  const callerAddress = identityIntact ? (prefill?.caller_address ?? null) : null
  /** Known caller, no address on record. Different from an unknown caller, and
   *  different again from us not having looked -- saying nothing leaves a
   *  dispatcher who picked "Himself" unable to tell which. */
  const callerHasNoAddress =
    identityIntact && !!prefill?.contact_id && !prefill?.caller_address

  // Whether the location currently on screen was put there by us rather than
  // typed. Changing the relationship may replace OUR text; it must never
  // replace the dispatcher's, because he is looking at the caller and we are
  // looking at a database row.
  const [autoFilled, setAutoFilled] = useState(draft?.autoFilled ?? false)

  /** Choosing "calling for" is what decides whether the caller's own address
   *  is the incident's address.
   *
   *  PLAN.html says exactly this: caller relationships "drive whether the
   *  caller's own address auto-fills at intake". The flag has existed on every
   *  relationship since migration 003 and nothing read it.
   *
   *  The order matters and is why this cannot simply prefill on the pop: at
   *  the moment the phone rings nobody has asked who they are calling for. A
   *  passer-by reporting a crash three streets away would have had his home
   *  address written into the incident before he finished his first sentence,
   *  and responders sent to it. So the address waits for the answer. */
  function chooseRelationship(r: ListItem) {
    const fills = !!r.fills_caller_address
    const next = {
      ...form,
      relationship_id: r.id,
      relationship_label: r.name,
    }
    if (fills && callerAddress) {
      // Only over an empty box or over our own previous suggestion.
      if (!form.location_label || autoFilled) {
        next.location_label = callerAddress
        setAutoFilled(true)
      }
    } else if (autoFilled) {
      // He was calling for himself, now he is not. Take our suggestion back
      // rather than leaving somebody else's address on the incident.
      next.location_label = ''
      setAutoFilled(false)
    }
    setForm(next)
  }

  // -------------------------------------------------------------------------
  // The nature, and the questions behind it
  // -------------------------------------------------------------------------

  /** What the dispatcher has typed to narrow the natures down. */
  const [filter, setFilter] = useState('')
  /** Which row the arrow keys are on. Reset whenever the list changes under
   *  it, or Enter opens whichever nature happens to have slid into that slot. */
  const [highlight, setHighlight] = useState(0)
  /** How many questions each nature asks. Only to say so on the row and to
   *  decide what is worth prefetching — the trees themselves come one at a
   *  time. */
  const [counts, setCounts] = useState<Record<string, number>>({})
  /** The FIRST nature's questions. */
  const [tree, setTree] = useState<CallQuestion[]>([])
  const [answers, setAnswers] = useState<Record<string, string>>(draft?.answers ?? {})
  const [treeError, setTreeError] = useState<string | null>(null)
  /** THE OTHER NATURES (076) and their questions, by nature id. */
  const [extras, setExtras] = useState<{ id: string; name: string }[]>(draft?.extraNatures ?? [])
  const [extraTrees, setExtraTrees] = useState<Record<string, CallQuestion[]>>({})
  /** The picker is choosing ANOTHER nature rather than replacing the first. */
  const [adding, setAdding] = useState(false)
  /** Which nature `tree` is the questions of, once they have arrived. */
  const [treeOf, setTreeOf] = useState('')
  const pickerRef = useRef<HTMLInputElement>(null)
  /** EVERY NATURE'S QUESTIONS, ONE TREE, a question asked once -- what the
   *  call will ask (`call_questions.tree_for_natures`). */
  const asked = mergeTrees([tree, ...extras.map((n) => extraTrees[n.id] ?? [])])
  /** A NATURE WHOSE QUESTIONS HAVE NOT ARRIVED -- a restored draft's, still
   *  loading or failed. Its answers are not in `asked` yet, and pruning
   *  against `asked` would delete them and the draft would save without
   *  them; so while any is missing, an answer to a question not drawn is
   *  KEPT. */
  const treesPending = (!!form.nature_id && treeOf !== form.nature_id)
    || extras.some((n) => !(n.id in extraTrees))
  /** THE CALLER NOT STAYING (077) asks what is asked only then -- "Where will
   *  the keys be?" -- and those questions are drawn right under the Staying /
   *  Not staying buttons (his Q2 A), the rest where they always were. */
  const asking = { callerLeaving: form.caller_on_scene === false }
  /** What a press keeps: everything the caller's box alone is hiding. */
  const keepAll = { callerLeaving: true }
  const afterStay = underStaying(asked)
  function changeAnswers(next: Record<string, string>) {
    if (!treesPending) { setAnswers(next); return }
    const drawn = new Set(asked.map((q) => q.key))
    setAnswers({
      ...Object.fromEntries(Object.entries(answers).filter(([key]) => !drawn.has(key))),
      ...next,
    })
  }

  /** Trees already fetched, FOR THE LIFE OF THIS FORM AND NO LONGER.
   *
   *  It is what makes prefetching on the highlight worth doing: arrowing down a
   *  list of thirty natures fetches each one once, and pressing Enter draws the
   *  questions with no wait at all. It deliberately does not outlive the form —
   *  a cache that survived would be yesterday's tree on tomorrow's call, which
   *  is exactly the staleness that fetching per nature exists to avoid. */
  const trees = useRef<Map<string, CallQuestion[]>>(new Map())

  useEffect(() => {
    // Read when the form opens rather than with the nature list on the
    // dispatch screen: it is one small row per nature, it is always current,
    // and a failure costs a count on a row rather than the ability to take a
    // call. Nothing below waits for it.
    api.questionNatureCounts()
      .then((rows) => setCounts(
        Object.fromEntries(rows.map((r) => [r.nature_id, r.questions]))))
      .catch(() => {})
  }, [])

  const live = natures.filter((n) => !n.retired_at)
  // HIS OWN REASONING FOR A FLAT LIST: "once we start typing in lockout on the
  // drop down of the natures, it will bring up all the lockout natures that are
  // available. So does not need to be a main category for lockout and sub." The
  // naming does the grouping and this does the filtering -- and a code typed
  // here (FT) puts its nature first, which is what the codes are for.
  const matches = findNatures(live, filter)
  /** Open when he is typing, or when he asked for the whole list.
   *
   *  SHUT AFTER A PICK BECAUSE `pick` CLEARS BOTH, not because a nature is
   *  chosen. Those read the same on a fresh form and are opposite things on a
   *  call: the caller says "flat tire" and then "actually I'm locked out", and
   *  a list that can never reopen leaves Cancel -- losing his name, number,
   *  address, pin and every answer -- as the only way to correct it. */
  const listShown = filter.trim().length > 0 || browsing
  /** The spot currently on screen: the coordinates belong to the address in
   *  the box, not to whatever was there two keystrokes ago. */
  /** WHERE THE CALL IS, as typed: the address, or the details with none. */
  const whereNow = form.location_label.trim() || details.trim()
  const pinnedSpot = spot && spot.label
    && (spot.label === form.location_label.trim() || spot.label === details.trim()) ? spot : null
  /** `address` -- whether this pin is the ADDRESS's: the server geocodes an
   *  address's place on its own, and nothing places a details-only call. */
  const pinned = pinnedSpot
    ? { ...pinnedSpot, address: !pinnedSpot.wide && pinnedSpot.label === form.location_label.trim() }
    : null
  const coded = live.some((n) => n.code)
  const hot = Math.min(highlight, Math.max(0, matches.length - 1))

  /** Suggestions while he types, from this organization's own address book.
   *
   *  DEBOUNCED, and not because of a rate limit -- this is our own database --
   *  but because a request per keystroke on a dispatcher's slow connection
   *  arrives out of order, and the list then flickers between answers to
   *  "42 O" and "42 Oak". */
  useEffect(() => {
    const typed = form.location_label.trim()
    // SETTLED MEANS SETTLED. Picking one from the list, or finishing a lookup,
    // answers the question this list exists to ask -- and a list that reopens
    // over the answer covers the map underneath it.
    if (typed.length < 2 || settled.current === typed) {
      setOffered([])
      return
    }
    let alive = true
    const timer = setTimeout(() => {
      api.placeSuggest(typed, session.current)
        .then((found) => {
          // `alive` IS THE ORDERING GUARD. Two requests in flight can answer
          // out of order, and the older one would otherwise replace the list
          // with suggestions for "42 O" while he is looking at "42 Oak".
          if (!alive) return
          setOffered(found.suggestions)
          setSuggestNote(found.note)
        })
        .catch(() => { if (alive) setOffered([]) })
    }, 180)
    return () => { alive = false; clearTimeout(timer) }
  }, [form.location_label, spot])

  /** Ask the map where this address is. ONE DELIBERATE LOOKUP, when the
   *  address is settled -- a suggestion picked, or the box left -- never per
   *  keystroke: OpenStreetMap's policy names autocomplete as a way to get an
   *  application blocked, and the server's throttle is the other half of that.
   *
   *  IT CANNOT FAIL THE CALL. Every outcome is a sentence beside the box; the
   *  broadcast goes with or without a pin. */
  async function findOnMap(label: string, again = false, box: 'address' | 'details' = 'address') {
    const typed = label.trim()
    // EACH BOX ITS OWN BOOKKEEPING (the review of 2139ec7): a lookup of the
    // details must not mark the ADDRESS settled -- that reopened the address
    // suggestions over the map, a billed request at Google levels -- nor
    // count as the address having been answered.
    const answeredRef = box === 'address' ? answered : detailsAnswered
    // `again` is the Find on map button: asked on purpose, it asks again.
    if (!typed || (!again && answeredRef.current === typed) || inFlight.current === typed) return
    /** Still what is in THIS box. */
    const stillThere = () =>
      typed === (box === 'address' ? labelNow.current : detailsNow.current).trim()
    inFlight.current = typed
    if (box === 'address') settled.current = typed
    setLooking(true)
    setMapSaid(null)
    try {
      const found = await api.placeLocatePreview(typed)
      // STALE ANSWERS ARE DROPPED. Two lookups can be in flight -- he tabbed
      // out of "42 Oak", back in, and out of "42 Oak Street" -- and the
      // geocoder is throttled, so the older one can land last. Without this
      // the earlier failure erases the later pin and tells him to ask for a
      // cross street for an address the map found.
      if (!stillThere()) return
      answeredRef.current = typed
      // A PIN HE PLACED HIMSELF IS NEVER REPLACED OR ERASED BY A LOOKUP (the
      // review of 2139ec7): it is his claim about where the caller is.
      if (found.found && found.latitude !== null && found.longitude !== null) {
        setSpot((s) => (s?.moved ? s
          : { label: typed, lat: found.latitude!, lon: found.longitude!, moved: false }))
      } else {
        setSpot((s) => (s?.moved ? s : null))
        setMapSaid(found.detail
          ?? 'The map does not know this place. Ask for a cross street, or put '
             + 'the pin on the map yourself — the call still goes out exactly as you typed it.')
      }
    } catch {
      if (!stillThere()) return
      setSpot((s) => (s?.moved ? s : null))
      setMapSaid('The map could not be reached. The call still goes out.')
    } finally {
      if (inFlight.current === typed) inFlight.current = ''
      setLooking(false)
    }
  }

  /** THE PIN, PUT BY HAND (071, his Q2 A): the map opens on the service area
   *  with a pin in the middle, and only once he drags it or clicks the map is
   *  it a claim the call carries -- `moved` stays false until then, so a pin
   *  left in the middle of town is never sent as where somebody is. */
  async function dropPin() {
    const s = await mapSettings()
    if (!s?.centre) {
      setMapSaid((s && !s.enabled
        ? 'No map is switched on for this customer, so a pin cannot be placed. '
        : 'The map could not be opened just now. ')
        + 'The call still goes out as typed.')
      return
    }
    if (!whereNow) return
    setMapSaid(null)
    setSpot({ label: whereNow, lat: s.centre.latitude, lon: s.centre.longitude,
              moved: false, wide: true })
  }

  /** He picked a suggestion. One that carries its coordinates -- an address
   *  of his own already geocoded, or a Photon result precise enough to pin --
   *  draws the map with no lookup at all. */
  async function takeSuggestion(s: {
    label: string
    latitude: number | null
    longitude: number | null
    cross_street_a?: string | null
    cross_street_b?: string | null
    provider_place_id: string | null
    source: 'book' | 'map'
  }) {
    setAutoFilled(false)
    setForm((current) => ({ ...current, location_label: s.label }))
    // TYPED ONCE, FILLED IN NEXT TIME -- into empty boxes, or over a pair an
    // earlier pick filled in and nobody touched. Cross streets he TYPED on this
    // call are what the caller just told him, and are never replaced.
    const knownA = s.cross_street_a
    const knownB = s.cross_street_b
    const empty = !cross.a.trim() && !cross.b.trim()
    if (empty || crossIsOurs(cross)) {
      if (knownA && knownB) {
        crossFilled.current = { label: s.label.trim(), a: knownA, b: knownB }
        setCross({ a: knownA, b: knownB })
      } else if (!empty) {
        crossFilled.current = null
        setCross({ a: '', b: '' })
      }
    }
    setOffered([])
    setMapSaid(null)
    const label = s.label.trim()

    // ALREADY ON THE MAP -- one of his own, or a Photon suggestion: no request.
    if (s.latitude !== null && s.longitude !== null) {
      answered.current = label
      settled.current = label
      setSpot({ label, lat: s.latitude, lon: s.longitude, moved: false })
      return
    }

    // ONE THE MAP OFFERED: resolved inside the session that found it, which
    // is what makes the whole typing session one charge. The session is spent
    // afterwards, so the next address starts a new one.
    if (s.source === 'map' && s.provider_place_id) {
      answered.current = label
      settled.current = label
      setLooking(true)
      try {
        const at = await api.placeResolveSuggestion(
          s.provider_place_id, session.current)
        if (at.found && at.latitude !== null && at.longitude !== null) {
          setSpot({ label, lat: at.latitude, lon: at.longitude, moved: false })
        } else {
          setSpot(null)
          setMapSaid(at.detail ?? 'The map could not place that suggestion.')
        }
      } catch {
        setSpot(null)
        setMapSaid('The map could not be reached. The call still goes out.')
      } finally {
        session.current = newSession()
        setLooking(false)
      }
      return
    }

    // One of his own that has never been geocoded, or -- at the Google search
    // boost -- Google's suggestion, positioned by the organization's own
    // OpenStreetMap search: an ordinary lookup of the picked label.
    labelNow.current = s.label
    // A PICK ENDS THE BILLING SESSION whether or not Google resolved it, so the
    // next address typed starts a new one rather than riding on this one.
    if (s.source === 'map') session.current = newSession()
    findOnMap(s.label)
  }

  /** Fetch a tree into the cache without disturbing what is on screen. */
  async function warm(natureId: string): Promise<CallQuestion[]> {
    const held = trees.current.get(natureId)
    if (held) return held
    const fetched = await api.questionTree(natureId)
    trees.current.set(natureId, fetched.questions)
    return fetched.questions
  }

  // PREFETCH WHATEVER THE ARROW KEYS ARE SITTING ON. The dispatcher is on the
  // phone, so the questions should be on screen the instant he presses Enter
  // rather than a moment after it. The short delay is what stops typing three
  // letters from firing three requests for three natures he is passing over;
  // failures are swallowed, because this is an optimisation and `pick` fetches
  // for itself.
  const candidate = matches[hot]?.id
  useEffect(() => {
    if (!candidate) return
    if (!counts[candidate]) return
    if (trees.current.has(candidate)) return
    const t = setTimeout(() => { warm(candidate).catch(() => {}) }, 250)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candidate, counts])

  /** Which nature the questions on screen are supposed to belong to.
   *
   *  A LATER PICK MUST WIN, however slowly the earlier one answers. Choosing
   *  Flat tire and then Car lockout while the first request is still in flight
   *  would otherwise draw the flat-tire questions under the lockout heading —
   *  and the dispatcher's answers would then be dropped by the server, which is
   *  the one thing that must not happen quietly. */
  const wanted = useRef<string>('')

  /** A DRAFT COMES BACK WITH ITS QUESTIONS. `pick` is what loads a tree, and
   *  it clears the answers -- right for a new nature, wrong for one chosen
   *  before the dispatcher moved away. So a restored nature's tree is fetched
   *  here, once, and the saved answers stay as they were. */
  useEffect(() => {
    const id = form.nature_id
    if (!draft || !id) return
    wanted.current = id
    warm(id)
      .then((questions) => {
        if (wanted.current === id) { setTree(questions); setTreeOf(id) }
      })
      .catch((e) => {
        if (wanted.current !== id) return
        setTreeError(e instanceof ApiError
          ? `The questions for this nature could not be loaded — ${e.message}`
          : 'The questions for this nature could not be loaded.')
      })
    for (const extra of draft.extraNatures ?? []) loadExtra(extra.id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /** EVERY CHANGE, HANDED UP TO BE SAVED. The whole form each time: the
   *  screen above decides when to save, and compares with what it last saved
   *  so an unchanged form is not written. */
  /** The whole form, as a draft holds it. */
  const snap = (): IntakeDraft => ({
    v: 1, prefill: prefill ?? null, form, urgency, callerUrgency, said, spot,
    cross, anon, locNote, others, area, audience, answers, autoFilled,
    noteInternal: notes.internal, noteResponder: notes.responder,
    locDetails: details,
    crossFilled: crossFilled.current,
    extraNatures: extras,
    past,
  })
  useEffect(() => {
    onSnapshot?.(snap())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, urgency, callerUrgency, said, spot, cross, anon, locNote, others,
      area, audience, answers, autoFilled, details, extras, past, notes])

  /** Choose a nature, and load what it asks.
   *
   *  THE ANSWERS GO WITH THE OLD NATURE. They are keyed by a question's key and
   *  the new nature asks different questions; carrying them across would send
   *  the server keys this call does not ask, which it drops and names — a
   *  dispatcher told his answers did not stick, for something he never did. */
  /** Fetch one of the other natures' questions into `extraTrees`. */
  function loadExtra(id: string) {
    warm(id)
      .then((questions) => setExtraTrees((held) => ({ ...held, [id]: questions })))
      .catch((e) => setTreeError(e instanceof ApiError
        ? `The questions for one of the natures could not be loaded — ${e.message}`
        : 'The questions for one of the natures could not be loaded.'))
  }

  /** ANOTHER NATURE ON THIS CALL (076): "he needs a boost ... and the car is
   *  also locked". Its questions are asked as well; one both ask, once. */
  function addExtra(nature: ListItem) {
    setAdding(false)
    setBrowsing(false)
    setFilter('')
    setHighlight(0)
    if (nature.id === form.nature_id || extras.some((n) => n.id === nature.id)) return
    setExtras((now) => [...now, { id: nature.id, name: nature.name }])
    loadExtra(nature.id)
  }

  /** Take one of the other natures off again. Answers only it asked go with it. */
  function dropExtra(id: string) {
    const left = extras.filter((n) => n.id !== id)
    setExtras(left)
    const still = mergeTrees([tree, ...left.map((n) => extraTrees[n.id] ?? [])])
    const keys = new Set(still.map((q) => q.key))
    // KEEPING what only the caller's box hides (077): taking a nature off
    // must not take "Where will the keys be?" with it.
    setAnswers((now) => prune(still, Object.fromEntries(
      Object.entries(now).filter(([k]) => keys.has(k))), keepAll))
  }

  async function pick(nature: ListItem) {
    if (adding) { addExtra(nature); return }
    // CHOOSING THE ONE ALREADY CHOSEN IS NOT A CHANGE. Enter pressed twice, or
    // a second click on the same row, would otherwise clear every answer the
    // dispatcher has just given — for a nature that did not move.
    if (nature.id === form.nature_id) return
    wanted.current = nature.id
    // SHUT IT DELIBERATELY. The list used to be latched shut by "a nature is
    // chosen", which also latched out changing his mind.
    setBrowsing(false)
    setFilter('')
    setHighlight(0)
    setForm((current) => ({
      ...current, nature_id: nature.id, nature_label: nature.name,
    }))
    // THE OTHER NATURES STAY, AND SO DO THEIR ANSWERS (076); the old first
    // nature's go with it. One that is now the first is not also another --
    // and keeps its answers, being still on the call.
    const left = extras.filter((n) => n.id !== nature.id)
    setExtras(left)
    const kept = new Set(mergeTrees([extraTrees[nature.id] ?? [],
      ...left.map((n) => extraTrees[n.id] ?? [])]).map((q) => q.key))
    setAnswers((now) => Object.fromEntries(Object.entries(now).filter(([k]) => kept.has(k))))
    setTree([])
    setTreeError(null)
    try {
      const questions = await warm(nature.id)
      if (wanted.current !== nature.id) return
      setTree(questions)
      setTreeOf(nature.id)
    } catch (e) {
      if (wanted.current !== nature.id) return
      // NAMED, AND IT BLOCKS NOTHING. A dead question tree costs the form its
      // questions; the call still opens, and they can be answered on the
      // working screen afterwards.
      setTreeError(e instanceof ApiError
        ? `The questions for this nature could not be loaded — ${e.message}`
        : 'The questions for this nature could not be loaded.')
    }
  }

  function onPickerKey(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      // THE FIRST PRESS OPENS THE WHOLE LIST rather than moving a highlight
      // nobody can see -- the list is shut until he asks for it.
      if (!listShown) { setBrowsing(true); setHighlight(0); return }
      setHighlight(Math.min(hot + 1, matches.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHighlight(Math.max(hot - 1, 0))
    } else if (e.key === 'Enter') {
      // ALWAYS PREVENTED, chosen or not. This input sits inside the form, so
      // an unhandled Enter submits it — opening a call with whatever is in the
      // boxes, from the field the dispatcher is still using to find the nature.
      e.preventDefault()
      // AND IT CANNOT CHOOSE FROM A LIST NOBODY CAN SEE. With the list shut,
      // Enter would take `matches[0]` — silently swapping the nature and
      // deleting every answer to the old one's questions.
      if (!listShown) return
      const choice = matches[hot]
      if (choice) pick(choice)
    } else if (e.key === 'Escape' && (filter || browsing || adding)) {
      // Only when there is something to shut or clear. Otherwise this is the
      // one key that could plausibly mean "abandon the call", and it must not.
      e.preventDefault()
      setBrowsing(false)
      setFilter('')
      setAdding(false)
    }
  }

  const stillToAsk = outstanding(asked, answers, asking)
  const stillHere = stillToAsk.filter((q) => !afterStay.has(q.key))

  /** WHERE THE CURSOR STARTS: the caller's name, or the address when the name
   *  and number are already there (the caller-ID pop, or a draft that got
   *  that far). Read once, on the form's first render. */
  const [startAtAddress] = useState(
    () => !!form.caller_name.trim() && !!form.caller_phone.trim() && !form.location_label.trim())

  /** The call code as it stands, redrawn with every answer so the dispatcher
   *  can say it on the air before the call exists. A preview: once the call is
   *  open the working screen shows the code the server froze. Read off the
   *  list by id, because `pick` keeps only the id and the name. */
  const code = callCodes(
    [form.nature_id, ...extras.map((n) => n.id)]
      .map((id) => natures.find((n) => n.id === id)?.code), asked, answers, asking)
  /** "Boost + Car Lockout", as the call will be called. */
  const allNatures = [form.nature_label, ...extras.map((n) => n.name)]

  /** Whether an answer will raise this call to Critical when it opens -- the
   *  server's rule (`allow_priority_raise = priority is None`), so the preview
   *  prints the urgency word the real broadcast will. */
  const raised = urgency === null
    && visible(asked, answers, asking).some((q) => q.raises_priority && alerting(q, answers[q.key] ?? ''))

  /** WHAT YOUR MEMBERS WILL RECEIVE, composed by the server as they type.
   *
   *  THE SERVER COMPOSES IT, NOT THIS FILE: the house-number rule, the "between"
   *  wording, which answers add a line and the header and footer all live
   *  there, and a second copy here is a preview that can disagree with the
   *  message. Debounced, and an answer arriving for text no longer in the
   *  boxes is dropped, like the address suggestions. A failure shows nothing
   *  rather than a stale message: the call still goes. */
  useEffect(() => {
    if (!mayBroadcast || past.on || !form.nature_label.trim() || !whereNow) {
      setWillGet(null)
      return
    }
    let alive = true
    const timer = setTimeout(() => {
      api.incidentPreviewBroadcast({
        nature_id: form.nature_id || null,
        nature_label: form.nature_label,
        location_label: form.location_label,
        location_details: details.trim() || null,
        cross_street_a: cross.a.trim() || null,
        cross_street_b: cross.b.trim() || null,
        broadcast_area: area,
        priority: urgency ?? (raised ? 1 : undefined),
        answers,
        extra_natures: extras.map((n) => n.id),
        caller_on_scene: form.caller_on_scene,
      })
        .then((p) => { if (alive) setWillGet(p) })
        .catch(() => { if (alive) setWillGet(null) })
    }, 350)
    return () => { alive = false; clearTimeout(timer) }
  }, [mayBroadcast, past.on, form.nature_id, form.nature_label, form.location_label,
      cross.a, cross.b, area, urgency, raised, answers, details, extras,
      form.caller_on_scene])

  /** What a flagged answer has done, or will do, to the urgency.
   *
   *  TWO STATES AND THEY ARE THE SERVER'S, not a guess: `call_questions.record`
   *  is passed `allow_priority_raise = priority is None`, so the raise happens
   *  only when the dispatcher has chosen no urgency of his own. If he has, the
   *  answer is written into the thread and the urgency is left where he put it.
   *  Saying "this goes out Critical" in that case would be a promise the server
   *  is not going to keep. */
  function alertNote(question: CallQuestion): string {
    if (!question.raises_priority) {
      return 'Flagged — this answer shows in red on the ticket and the queue.'
    }
    if (urgency === null) {
      return `Flagged — this call goes out at ${ALERT_LABEL}.`
        + ' Press an urgency in step 4 to decide it yourself instead.'
    }
    const chosen = PRIORITIES.find((p) => p.v === urgency)?.label ?? `priority ${urgency}`
    return `Flagged in red — the urgency stays at ${chosen} because you set it.`
  }

  /** WHICH STEP IS ON SCREEN. A call from the caller-ID pop already has his
   *  name and number, so it opens on Where -- the same rule that put the
   *  cursor in the address box before. */
  const [step, setStep] = useState<Step>(() => (startAtAddress ? 2 : 1))
  const stepRef = useRef<HTMLDivElement>(null)
  const cardRef = useRef<HTMLDivElement>(null)
  /** Go to a step. LEAVING WHERE ASKS THE MAP, as leaving the address box
   *  always has: a hidden box is not blurred in every browser, and the pin
   *  is what the ticket shows. `findOnMap` asks nothing twice. */
  function goStep(next: Step) {
    if (next === step) return
    if (step === 2) {
      if (form.location_label.trim()) findOnMap(form.location_label)
      else if (details.trim()) findOnMap(details, false, 'details')
    }
    setStep(next)
  }
  // THE FIRST BOX OF A STEP TAKES THE CURSOR when the step is chosen, so a
  // keyboard never has to hunt for it -- not on the first draw, where the
  // form's own autofocus decides.
  const firstDraw = useRef(true)
  useEffect(() => {
    if (firstDraw.current) { firstDraw.current = false; return }
    stepRef.current?.querySelector<HTMLElement>(
      `[data-step="${step}"] input:not([type="checkbox"])`)?.focus()
  }, [step])
  // F1-F4 GO STRAIGHT TO A STEP -- ONLY WHILE THIS FORM HAS THE KEYBOARD.
  // Never over a dialog (Rebroadcast, the caller card, Change my password all
  // open over a live form), never with a modifier held, never for a key
  // somebody else already handled: switching the step behind a dialog pulled
  // the cursor out of it and asked the map about an address mid-sentence.
  useEffect(() => {
    const onKey = (e: globalThis.KeyboardEvent) => {
      const at = ['F1', 'F2', 'F3', 'F4'].indexOf(e.key)
      if (at < 0 || e.defaultPrevented || e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return
      if (document.querySelector('[aria-modal="true"]')) return
      const on = document.activeElement
      if (on && on !== document.body && !cardRef.current?.contains(on)) return
      e.preventDefault()
      goStep((at + 1) as Step)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  })
  /** Who it goes to, in words: everyone on duty, or the groups pressed. */
  const audienceText = audience.length === 0
    ? `everyone on duty${audiences ? ` (${audiences.everyone})` : ''}`
    : (audiences?.groups ?? []).filter((g) => audience.includes(g.id)).map((g) => g.name).join(', ')
      || `${audience.length} groups`
  /** The step names, with step 4 saying what it holds on a call that already
   *  happened. */
  const stepNames = past.on ? [...STEP_NAMES.slice(0, 3), 'Times & who went'] : STEP_NAMES
  /** What each step holds so far, in a few words, for its button. */
  const stepSummary: Record<Step, string> = {
    1: anon ? 'Anonymous'
      : form.caller_name.trim() || (form.caller_phone.trim()
        ? `…${form.caller_phone.replace(/\D/g, '').slice(-4)}` : 'Who is calling'),
    2: form.location_label.trim() || details.trim() || 'The address',
    3: form.nature_label ? allNatures.join(' + ') : 'Nature and questions',
    4: past.on
      ? (pastLine(past) || 'When, who went, how it ended')
      : `${urgency !== null ? PRIORITIES.find((p) => p.v === urgency)?.label : 'Urgency'} · ${mayBroadcast ? audienceText : 'the call'}`,
  }
  /** The groups ticked reach nobody on duty -- the one audience the server
   *  would refuse after the call had opened, so Create waits for it. */
  const aimedAtNobody = !past.on && mayBroadcast && audience.length > 0 && reachesNobody(audiences, audience)
  /** What still stops Save on a call that already happened. */
  const pastNeeds = logging ? pastMissing(past) : null
  const done: Record<Step, boolean> = {
    1: anon || !!form.caller_name.trim() || !!form.caller_phone.trim(),
    2: !!whereNow,
    3: !!form.nature_label.trim(),
    4: past.on ? !pastNeeds : urgency !== null && !aimedAtNobody,
  }
  /** The answers given so far, as the ticket prints them. */
  const ticketAnswers = visible(asked, answers, asking)
    .filter((q) => picked(q, answers[q.key] ?? '').length > 0)
    .map((question) => ({
      question,
      text: question.kind === 'vehicle'
        ? vehicleText(decodeVehicle(answers[question.key]))
        : answerText(question, answers[question.key] ?? ''),
    }))
  /** Why Create cannot be pressed, said beside it: the two things the server
   *  itself refuses. Nothing else ever holds a call up. */
  const missing = pastLocked ? 'A call that already happened — your role cannot save one'
    : !form.nature_label.trim() && !whereNow ? 'Needs the address and the nature'
    : !whereNow ? 'Needs the address' : !form.nature_label.trim() ? 'Needs the nature'
      : aimedAtNobody ? 'The groups ticked reach nobody on duty (step 4)' : pastNeeds

  async function submit(e: FormEvent) {
    e.preventDefault()
    if (pastLocked) return
    if (logging) { await saveLogged(); return }
    setBusy(true)
    onBusy?.(true)
    try {
      // THE SAME BODY A DRAFT IS OPENED WITH from "Broadcast drafts" (074):
      // `openBody` is the one definition, read off the form as a draft holds it.
      const created = await api.incidentOpen(openBody(snap(), draftId ?? null))
      // WHAT DID NOT STICK, SAID AFTER THE CALL IS ON SCREEN. Nothing here is
      // fatal — the call is open and the answers can be retyped on it — but a
      // dropped answer that nobody mentions is a fact the dispatcher believes
      // he recorded.
      const outcome = created.answers
      const trouble: string[] = []
      if (outcome?.refused) trouble.push(outcome.refused)
      // AN ANSWER STORED DIFFERENTLY SAYS WHY. Counted in with the rest, a
      // vehicle dropped for its year was reported as "the questions changed
      // while this form was open", which sends the dispatcher looking for an
      // edit nobody made. `Object.hasOwn`, not `in`: a question keyed
      // `constructor` is "in" every object.
      const notes = outcome?.notes ?? {}
      trouble.push(...Object.values(notes))
      const stale = (outcome?.dropped ?? []).filter((key) => !Object.hasOwn(notes, key))
      if (stale.length) {
        trouble.push(
          `${stale.length} answer(s) were not stored because the`
          + ' questions changed while this form was open. Answer them again on'
          + ' the call.')
      }
      // AND THE BROADCAST, which is what the button has always said it does.
      // It went out on a SECOND press until now: the call opened, the form
      // closed, and nothing was sent to anybody until somebody noticed the
      // green button on the working call.
      //
      // AFTER THE CALL IS COMMITTED, and never inside it. A send that fails --
      // a channel switched off, a provider refusing -- must not cost the
      // incident, and this way it cannot: the call is already open, and the
      // failure arrives as a sentence with a Broadcast button beside it.
      if (mayBroadcast) {
        try {
          await api.incidentBroadcast(created.id, 'initial', audience)
        } catch (e) {
          trouble.push(e instanceof ApiError
            ? `The call is open but the broadcast did not go out — ${e.message} `
              + 'Press Broadcast on the call.'
            : 'The call is open but the broadcast did not go out. Press '
              + 'Broadcast on the call.')
        }
      }
      if (created.numbers?.note) trouble.push(created.numbers.note)
      if (created.notes?.note) trouble.push(created.notes.note)
      onDone(created.id, trouble.join(' ') || null)
    } catch (e) {
      onError(e instanceof ApiError ? e.message : 'Could not open the call.')
    } finally {
      setBusy(false)
      onBusy?.(false)
    }
  }

  /** SAVE A CALL THAT ALREADY HAPPENED (078). The body a live call opens
   *  with (`openBody`) plus the times and who went; nothing is sent, so no
   *  broadcast follows. Closed, it goes to Past calls. */
  async function saveLogged(anyway = false) {
    // THE CALL'S OWN TIMES LEFT EMPTY ARE WARNED ABOUT, NEVER REFUSED (083,
    // the customer: "a warning by saving it ... continue, yes or no").
    if (!anyway && pastTimesMissing(past).length) {
      setTimesWarn(true)
      return
    }
    setTimesWarn(false)
    setBusy(true)
    onBusy?.(true)
    try {
      const saved = await api.incidentLogAfterwards({
        ...openBody(snap(), draftId ?? null), ...pastBody(past),
      })
      const outcome = saved.answers
      const trouble: string[] = []
      if (outcome?.refused) trouble.push(outcome.refused)
      trouble.push(...Object.values(outcome?.notes ?? {}))
      if (saved.numbers?.note) trouble.push(saved.numbers.note)
      if (saved.notes?.note) trouble.push(saved.notes.note)
      onDone(saved.id, trouble.join(' ') || null, { closed: !past.open, number: saved.number })
    } catch (e) {
      onError(e instanceof ApiError ? e.message : 'Could not save the call.')
    } finally {
      setBusy(false)
      onBusy?.(false)
    }
  }

  return (
    <div className="card intake stepped" ref={cardRef}>
      <div className="dr-head">
        <h2>Take a call</h2>
        {draftId && <span className="dr-tag">Draft</span>}
        {saved && <span className="dr-saved">{saved}</span>}
      </div>

      {/* TAKING A CALL NOW, OR ONE THAT ALREADY HAPPENED (078). Drawn only for
          the tick; switching keeps everything typed. */}
      {mayLog && (
        <div className="imode" role="radiogroup" aria-label="What kind of call">
          <button type="button" role="radio" aria-checked={!past.on}
                  className={`imode-b ${!past.on ? 'on' : ''}`}
                  onClick={() => setPast((p) => ({ ...p, on: false }))}>
            <b>Taking a call now</b>
            <small>{mayBroadcast ? 'Create and broadcast, as always' : 'Create the call'}</small>
          </button>
          <button type="button" role="radio" aria-checked={past.on}
                  className={`imode-b log ${past.on ? 'on' : ''}`}
                  onClick={() => setPast((p) => ({ ...p, on: true }))}>
            <b>Log a call that already happened</b>
            <small>Pen and paper — Save, nothing is sent</small>
          </button>
        </div>
      )}
      {pastLocked && (
        <div className="notice">
          This draft is a call that already happened. Your role cannot save one — ask
          somebody who can, or discard it.
        </div>
      )}

      {/* THE FOUR STEPS (style B, the customer's Q1 B, 23 Oct 2026): "too much
          information and we need to scroll up and down to finish filling in
          everything". Each step is a page of its own; F1-F4 or a press goes
          straight to one, and every step stays mounted underneath -- hidden,
          never thrown away -- so a box half typed in is there on the way back. */}
      <div className="isteps" role="tablist" aria-label="Take a call">
        {stepNames.map((name, i) => {
          const n = (i + 1) as Step
          return (
            <button type="button" key={n} role="tab" aria-selected={step === n}
                    className={`istep-b ${step === n ? 'on' : ''} ${done[n] && step !== n ? 'ok' : ''}`}
                    onClick={() => goStep(n)}>
              <span className="n">{done[n] && step !== n ? '✓' : n}</span>
              <span className="t">{name}<small>{stepSummary[n]}</small></span>
              <span className="k">F{n}</span>
            </button>
          )
        })}
      </div>

      {/* ENTER FINISHES A BOX; ONLY THE BUTTON TAKES THE CALL. A single-line
          input submits its form on Enter, so pressing it after typing the
          plate, the caller's name or the callback number opened the call
          there and then -- closing the form onto the working call with the
          urgency never chosen and the rest of the boxes empty, and nothing
          saying it had happened. The nature picker handles its own Enter
          (and prevents this one), and every chip is a real button, which
          Enter still presses. ON THE LAST BOX OF A STEP it goes to the next
          step instead, which is how a keyboard walks the form. */}
      <form
        className="iform"
        onSubmit={submit}
        onKeyDown={(e) => {
          if (e.key !== 'Enter' || e.defaultPrevented) return
          const on = e.target as HTMLElement
          if (on instanceof HTMLInputElement && on.type !== 'submit') {
            e.preventDefault()
            const boxes = [...(stepRef.current?.querySelectorAll<HTMLInputElement>(
              `[data-step="${step}"] input:not([type="checkbox"])`) ?? [])]
            if (step < 4 && boxes.length > 0 && boxes[boxes.length - 1] === on) {
              goStep((step + 1) as Step)
            }
          }
        }}
      >
        <div className="ibody">
          <div className="istep" ref={stepRef}>
            {/* THIS MAY BE ABOUT A CALL THAT IS ALREADY OPEN (item 5). Same
                address first, then same nature, from the last hours. One press
                keeps this caller on that call; "Create a new call" below stays
                for when it really is a different incident. */}
            {similar.length > 0 && !past.on && (
              <div className="maybe">
                <div className="lbl">This may be about a call that is already open</div>
                {similar.map((s) => (
                  <div className="m" key={s.id}>
                    <span>
                      {s.call_code && <span className="callcode">{s.call_code}</span>}
                      <b>{s.number} {s.nature_label}</b>
                      {s.location && <span className="dim"> · {s.location}</span>}
                      {' '}
                      <span className={`pill ${s.responder_count ? 'go' : 'alarm'}`}>
                        {s.responder_count ? `${s.responder_count} going` : 'no response'}
                      </span>
                    </span>
                    <span className="why">
                      {s.why === 'address' ? 'same address' : 'same nature'}
                      {s.caller_count > 1 ? ` · ${s.caller_count} callers so far` : ''}
                    </span>
                    <span className="spacer" />
                    <button type="button" className="btn sm primary" disabled={!!joining}
                            onClick={() => joinCall(s.id)}>
                      {joining === s.id ? 'Adding…' : `Add this caller to ${s.number}`}
                    </button>
                  </div>
                ))}
                <div className="field" style={{ margin: '10px 0 0' }}>
                  <label htmlFor="said">What this caller adds</label>
                  <input id="said" maxLength={2000} value={said}
                         placeholder="Two cars, one on its side. A child is in the blue one."
                         onChange={(e) => setSaid(e.target.value)} />
                </div>
                <p className="footnote" style={{ margin: '6px 0 0' }}>
                  Adding keeps everything typed here — the caller, the number, what he
                  said, his answers — on that call as another report. No new call is
                  opened and nothing goes out unless you press Re-broadcast on it.
                </p>
              </div>
            )}


            <section data-step="1" hidden={step !== 1}>
            {/* WHO AND WHERE FIRST (the customer, 7 Oct 2026: "the first thing is
                the person's name, phone number, address, then continue with all
                the questions"). Calling for sits before the address (his Q1 A):
                "Himself" fills the address from the caller's record. Staying on
                scene stays with the rest further down (his Q2 A). */}
            <div className="grid2">
              <div className="field">
                <label>Caller name</label>
                <input
                  maxLength={200}
                  autoFocus={!startAtAddress}
                  value={form.caller_name}
                  onChange={(e) => setForm({ ...form, caller_name: e.target.value })}
                />
              </div>
              <div className="field">
                <label>Called from</label>
                <PhoneInput
                  maxLength={40}
                  value={form.caller_phone}
                  onChange={(e) => setForm({ ...form, caller_phone: e.target.value })}
                />
              </div>
            </div>
            {/* ANONYMOUS (item 4). A MARK, never a deletion: everything above and
                below is kept exactly as for any call; the call carries the flag,
                every screen shows it, and the server keeps the person out of
                every message. The customer: "if information entered my system,
                it should stay there forever." */}
            <label className={`check anonmark ${anon ? 'on' : ''}`} style={{ marginTop: -4 }}>
              <input type="checkbox" checked={anon} onChange={(e) => setAnon(e.target.checked)} />
              Anonymous — the caller does not want to be identified. Everything is
              kept in the system, but it is never given out: not in a text, not to a responder.
            </label>

            {/* THE TWO NOTES (081), two columns under the name and the number --
                for the look only, nothing to do with those two boxes. The same
                two texts are under the nature in step 3. */}
            <NotePair internal={notes.internal} responder={notes.responder}
                      anonymous={anon} onChange={setNote} />

            {/* OTHER NUMBERS, each with whose it is. Ten at most -- the server keeps
                the first ten and says so -- and a row left without a number is
                simply not sent. */}
            <div className="field">
              {others.length > 0 && <label>Other numbers to reach them</label>}
              {others.map((o, i) => (
                <div className="othernumber" key={i}>
                  <input
                    maxLength={40}
                    value={o.label}
                    placeholder="Home, wife…"
                    aria-label="Whose number"
                    onChange={(e) => setOthers(others.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))}
                  />
                  <PhoneInput
                    maxLength={40}
                    value={o.phone}
                    placeholder="Number"
                    aria-label="Number"
                    onChange={(e) => setOthers(others.map((x, j) => (j === i ? { ...x, phone: e.target.value } : x)))}
                  />
                  <button type="button" className="ghost tiny" title="Remove"
                          onClick={() => setOthers(others.filter((_, j) => j !== i))}>
                    ✕
                  </button>
                  {/* WHERE IT GOES IN THE PHONE BOOK (25 Oct 2026), for a role
                      that may add to it; the call keeps it either way. ON EVERY
                      ROW, typed or not: the customer looked at three empty rows
                      for the choice and concluded there was none. */}
                  {mayBook && (
                    <BookChoice save={o.save ?? 'no'} name={o.name ?? ''} label={o.label}
                                caller={anon ? '' : form.caller_name}
                                onChange={(save, name) => setOthers(others.map((x, j) => (j === i ? { ...x, save, name } : x)))} />
                  )}
                </div>
              ))}
              {others.length < 10 && (
                <button type="button" className="ghost tiny"
                        onClick={() => setOthers([...others, {
                          label: '', phone: '', save: mayBook ? 'caller' : 'no', name: '',
                        }])}>
                  + Add another number
                </button>
              )}
            </div>

            {/* THIS CALLER'S PAST CALLS, under the same tick as the ringing card --
                the customer's "A". Looked up by the number in the box, and nothing
                for a number two people share. */}
            <CallerPastCalls phone={form.caller_phone} />

            <div className="field">
              <label>Calling for</label>
              <div className="chips">
                {relationships.filter((r) => !r.retired_at).map((r) => (
                  <button
                    type="button"
                    key={r.id}
                    className={`chip ${form.relationship_id === r.id ? 'on' : ''}`}
                    onClick={() => chooseRelationship(r)}
                  >
                    {r.name}
                  </button>
                ))}
              </div>
              {/* SAID WHERE IT HAPPENED: Create is on every step, so the
                  address may never be looked at on step 2 before it goes. */}
              {autoFilled && (
                <p className="footnote provisional">
                  The address was filled in from the caller's record — {' '}
                  <strong>confirm it aloud</strong> (step 2).
                </p>
              )}
              {callerHasNoAddress && (
                <p className="footnote">No address on file for this caller — take it down in step 2.</p>
              )}
            </div>


            </section>

            <section data-step="2" hidden={step !== 2}>
            <div className="field">
              <label>Where is the incident?</label>
              {/* Visibly provisional while it is ours rather than his. At 3am the
                  difference between a typed address and a database-supplied one
                  cannot be a line of small grey text underneath. */}
              <input
                className={autoFilled ? 'provisional' : ''}
                maxLength={400}
                autoFocus={startAtAddress}
                value={form.location_label}
                placeholder="42 Oak Street"
                autoComplete="off"
                onChange={(e) => {
                  setAutoFilled(false)   // typed beats suggested, always
                  setForm({ ...form, location_label: e.target.value })
                  // `spot` IS NOT CLEARED HERE. It carries the label it belongs
                  // to, and `pinned` already refuses to draw it for any other --
                  // so typing a character and deleting it again brings the map
                  // back rather than losing it for the rest of the call.
                  setMapSaid(null)
                }}
                onBlur={() => findOnMap(form.location_label)}
                onKeyDown={(e) => {
                  // ENTER NEVER REPLACES WHAT HE TYPED (8 Oct 2026). It used to
                  // take the first suggestion, which turned a mile marker into
                  // some street; a suggestion is taken by clicking it. Enter and
                  // Escape close the list and leave the box as typed.
                  if ((e.key === 'Enter' || e.key === 'Escape') && offered.length > 0) {
                    e.preventDefault()
                    setOffered([])
                  }
                }}
              />
              {suggestNote && <p className="maphint warn">{suggestNote}</p>}
              {offered.length > 0 && (
                <div className="addrlist" role="listbox" aria-label="Addresses">
                  {offered.map((s) => (
                    <div
                      // `||`, not `??`: at the Google search boost every suggestion
                      // carries an EMPTY place id, and eight rows keyed "" leave
                      // stale rows on screen that can still be picked.
                      key={s.place_id ?? (s.provider_place_id || `map:${s.label}`)}
                      role="option"
                      aria-selected={false}
                      className="addropt"
                      // `onMouseDown`, not `onClick`: the click lands after the
                      // input's blur, and blur is what hides this list.
                      onMouseDown={(e) => { e.preventDefault(); takeSuggestion(s) }}
                    >
                      <span className="nm">{s.label}</span>
                      <span className="lbl">
                        {s.source === 'map' ? 'from the map'
                          : s.latitude !== null ? 'been here, on the map' : 'been here'}
                      </span>
                    </div>
                  ))}
                </div>
              )}
              {/* THE MAP, BEFORE ANYBODY IS SENT. The customer: "before hitting the
                  broadcast button, I should be able to see a preview of the map
                  where there is and to discuss it with the caller to make sure
                  that we are on the same page." Drag the pin and it is HIS
                  claim -- the loading bay, the shoulder, the far side of the
                  bridge -- recorded as his, on this call. */}
              {/* LOCATION DETAILS (071): an intersection, a highway and its
                  direction, a mile marker, a landmark. Kept as typed; the map is
                  asked as a try, and the call goes whether or not it answers. */}
              <label htmlFor="loc-details" className="ld-lab">Location details</label>
              <div className="ld-row">
                <input
                  id="loc-details"
                  maxLength={400}
                  autoComplete="off"
                  value={details}
                  placeholder="I-87 northbound, mile marker 23 · corner of Décarie and Van Horne"
                  onChange={(e) => {
                    const next = e.target.value
                    // A PIN PLACED FOR THESE DETAILS FOLLOWS THEIR EDITING: adding
                    // "right shoulder" must not hide it, or stop it being sent.
                    setSpot((s) => (s && (s.moved || s.wide) && s.label === details.trim()
                      ? { ...s, label: next.trim() } : s))
                    setDetails(next)
                    setMapSaid(null)
                  }}
                  onBlur={() => { if (!form.location_label.trim()) findOnMap(details, false, 'details') }}
                />
                <button type="button" className="btn sm" disabled={!details.trim() || looking}
                        onClick={() => findOnMap(details, true, 'details')}>
                  Find on map
                </button>
              </div>

              {/* Shown whenever we know the caller's own address, whether or not
                  the relationship has filled it in. Offering it is safe; putting
                  it there unasked is not. */}
              {callerAddress && form.location_label !== callerAddress && (
                <button
                  type="button"
                  className="chip"
                  style={{ marginTop: 8 }}
                  onClick={() => {
                    setForm({ ...form, location_label: callerAddress })
                    setAutoFilled(true)
                  }}
                >
                  Use the caller's own address — {callerAddress}
                </button>
              )}
              {autoFilled && (
                <p className="footnote provisional">
                  Filled in from the caller's record because of who they are calling
                  for — <strong>confirm it aloud</strong>. Type over it if the
                  incident is somewhere else.
                </p>
              )}
              {/* Absence stated as a fact. A dispatcher who has learned that
                  "Himself" fills the box picks it, sees nothing happen, and cannot
                  otherwise tell whether this caller has no address on file or the
                  feature is broken. */}
              {callerHasNoAddress && (
                <p className="footnote">
                  No address on file for this caller — take it down here and it can
                  be added to their record afterwards.
                </p>
              )}
            </div>

            <div className="field">
              <label htmlFor="cross-a">Between</label>
              {/* 120 each, the server's bound, held here too so a long one is
                  stopped while typing rather than refused on Create. */}
              <div className="between">
                <input
                  id="cross-a"
                  maxLength={120}
                  autoComplete="off"
                  value={cross.a}
                  placeholder="Cross street"
                  onChange={(e) => setCross({ ...cross, a: e.target.value })}
                />
                <span className="dim">and</span>
                <input
                  aria-label="and"
                  maxLength={120}
                  autoComplete="off"
                  value={cross.b}
                  placeholder="Cross street"
                  onChange={(e) => setCross({ ...cross, b: e.target.value })}
                />
              </div>
            </div>

            {/* THE NOTE ON THE LOCATION (item 3, 22 Sep 2026): instructions for
                finding the place on THIS call. Not a site note -- those live on
                the address and follow every call to it. Never in the broadcast;
                to the member who accepts, if the Messages tab says so. */}
            <div className="field">
              <label htmlFor="loc-note">Notes about the location</label>
              <input
                id="loc-note"
                maxLength={400}
                autoComplete="off"
                value={locNote}
                placeholder="Back entrance, ring twice; car is in the underground lot"
                onChange={(e) => setLocNote(e.target.value)}
              />
            </div>


            </section>

            <section data-step="3" hidden={step !== 3}>
            <div className="field natpick">
              <label htmlFor="naturefilter">Nature of call</label>
              {/* A FILTER BOX RATHER THAN A WALL OF BUTTONS, because the customer
                  intends to grow this list: "I have much more natures... House
                  Lockout, Fuel Delivery, Towing, Accident, Battery Replacement,
                  Motorcycle". Keyboard first — type, arrow, Enter — because the
                  dispatcher is on the telephone and taking a hand off it to aim a
                  mouse at one of forty buttons is the cost this replaces. */}
              <input
                id="naturefilter"
                ref={pickerRef}
                role="combobox"
                // ALL THREE FOLLOW THE LIST. They were written when it was always
                // rendered; with it shut, a screen reader was told an expanded
                // listbox existed, given the id of an element not in the document,
                // and pointed at an active option nobody could see.
                aria-expanded={listShown}
                aria-controls={listShown ? 'naturelist' : undefined}
                aria-autocomplete="list"
                aria-activedescendant={
                  listShown && matches[hot] ? `nat-${matches[hot].id}` : undefined
                }
                value={filter}
                placeholder={adding
                  ? 'Another nature for this call — type, or press ↓'
                  : 'Type to narrow — lockout, tire, or a code like FT…'}
                onChange={(e) => { setFilter(e.target.value); setHighlight(0) }}
                onKeyDown={onPickerKey}
              />
              {listShown && (
              <div className="natlist" id="naturelist" role="listbox" aria-label="Nature of call">
                {matches.length === 0 ? (
                  <div className="natnone">
                    {live.length === 0
                      ? 'No natures of call have been set up yet — Settings → Nature of call.'
                      : `Nothing matches “${filter.trim()}”.`}
                  </div>
                ) : matches.map((n, i) => (
                  /* A `div` with `role="option"`, not a button. Thirty buttons are
                     thirty tab stops between the filter box and the caller's name,
                     and this is a combobox: the arrow keys move it and Enter
                     chooses. Clicking still works, which is what a tablet does. */
                  <div
                    key={n.id}
                    id={`nat-${n.id}`}
                    role="option"
                    aria-selected={form.nature_id === n.id || extras.some((x) => x.id === n.id)}
                    className={`natopt ${i === hot ? 'hot' : ''}`}
                    onMouseMove={() => setHighlight(i)}
                    onClick={() => pick(n)}
                  >
                    {/* A CELL EVEN WHEN EMPTY, so the names start in one column
                        and a code reads as a column rather than as a prefix on
                        some of the names -- but only once the organization uses
                        codes at all, or every name is pushed right by nothing. */}
                    {coded && <span className="natcode">{n.code}</span>}
                    <span className="nm">{n.name}</span>
                    {counts[n.id] > 0 && (
                      <span className="lbl">{counts[n.id]} questions</span>
                    )}
                  </div>
                ))}
              </div>
              )}
              {!listShown && !form.nature_label && (
                <p className="nathint">
                  Type to narrow the list, or press <kbd>↓</kbd> to see all
                  {live.length > 0 ? ` ${live.length}` : ''}.
                </p>
              )}
              {form.nature_label && (
                <p className="natchosen">
                  Taking: {code && <><span className="callcode">{code}</span> </>}
                  <strong>{form.nature_label}</strong>
                  {/* THE OTHER NATURES (076), each with its own way off. */}
                  {extras.map((n) => (
                    <span key={n.id} className="natmore">
                      {' + '}<strong>{n.name}</strong>
                      <button type="button" className="natx" aria-label={`Take ${n.name} off`}
                              title="Take it off" onClick={() => dropExtra(n.id)}>✕</button>
                    </span>
                  ))}
                  {' '}
                  {form.nature_id && !adding && extras.length < 9 && (
                    <button type="button" className="natadd" onClick={() => {
                      setAdding(true); setBrowsing(true); setFilter(''); setHighlight(0)
                      pickerRef.current?.focus()
                    }}>+ Add another nature</button>
                  )}
                  {adding ? (
                    <span className="natchange"> — choose the other nature above (Esc to stop)</span>
                  ) : !extras.length && (
                    <span className="natchange"> — type, or press ↓, to change it</span>
                  )}
                </p>
              )}
              {treeError && <p className="qalert">{treeError}</p>}
            </div>

            {/* THE SAME TWO NOTES AS STEP 1 (081): he carries on writing here. */}
            <p className="cn-mirror">↔ The same notes as in step 1. Type here or there, it is one note.</p>
            <NotePair internal={notes.internal} responder={notes.responder}
                      anonymous={anon} onChange={setNote} short />

            {/* THE CONDITIONAL QUESTIONS. They appear as they become relevant and
                go away with their answers when an earlier answer changes — the
                rule lives in lib/questions.tsx and is the server's own, so the two
                cannot come to disagree about what is being asked. */}
            {asked.some((q) => !afterStay.has(q.key)) && (
              <div className="qform">
                <div className="qhead">
                  {/* NOT THE NATURE'S NAME, which is on the line directly above
                      this one. Saying it twice makes two headings out of one
                      fact and the second is the one that goes stale. */}
                  <h3>Questions</h3>
                  <span className="pill">
                    {/* THE QUESTIONS IN THIS BOX: the ones under Staying / Not
                        staying are counted where they are drawn (077). */}
                    {stillHere.length === 0
                      ? 'all answered'
                      : `${stillHere.length} unanswered`}
                  </span>
                </div>
                <div className="qgrid">
                <QuestionFields
                  tree={asked}
                  answers={answers}
                  alertNote={alertNote}
                  onChange={changeAnswers}
                  sections={allNatures}
                  asking={asking}
                  keep={keepAll}
                  only={(q) => !afterStay.has(q.key)}
                />
                </div>
              </div>
            )}

            {/* The operationally critical field. Someone who reports an incident
                and drives on means nobody is there to meet the responder, and the
                details taken were second-hand. He must know before setting off. */}
            <div className="field">
              <label>Is the caller staying on scene?</label>
              <div className="chips">
                <button
                  type="button"
                  className={`chip ${form.caller_on_scene ? 'on' : ''}`}
                  onClick={() => setForm({ ...form, caller_on_scene: true })}
                >
                  Staying
                </button>
                <button
                  type="button"
                  className={`chip alarm ${form.caller_on_scene === false ? 'on' : ''}`}
                  onClick={() => setForm({ ...form, caller_on_scene: false })}
                >
                  Not staying
                </button>
              </div>
              {/* ASKED BECAUSE HE IS LEAVING (077): "Where will the keys be?",
                  right under the button just pressed. The whole tree decides what
                  shows; this draws only the questions that belong here. */}
              {asking.callerLeaving
                && visible(asked, answers, asking).some((q) => afterStay.has(q.key)) && (
                <div className="qform qleaving">
                  <QuestionFields
                    tree={asked}
                    answers={answers}
                    alertNote={alertNote}
                    onChange={changeAnswers}
                    asking={asking}
                    keep={keepAll}
                    only={(q) => afterStay.has(q.key)}
                  />
                </div>
              )}
            </div>


            </section>

            <section data-step="4" hidden={step !== 4}>
            {past.on && (
              <PastCallFields value={past} onChange={setPast}
                              roster={roster} resolutions={resolutions} />
            )}
            {areas.length > 0 && !past.on && (
              <div className="field">
                <label>Broadcast only the area</label>
                <div className="chips">
                  {areas.map((g) => (
                    <button
                      type="button"
                      key={g.id}
                      className={`chip ${area === g.name ? 'on' : ''}`}
                      onClick={() => setArea(area === g.name ? null : g.name)}
                    >
                      {g.name}
                    </button>
                  ))}
                </div>
                {area && (
                  <p className="areahint">
                    Members see “{area}” instead of the street. Who receives it is
                    chosen below.
                  </p>
                )}
              </div>
            )}

            <div className="field">
              <label>Urgency</label>
              <div className="chips">
                {PRIORITIES.map((p) => (
                  <button
                    type="button"
                    key={p.v}
                    className={`chip ${urgency === p.v ? 'on' : ''}`}
                    onClick={() => setUrgency(urgency === p.v ? null : p.v)}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>

            {/* THE CALLER'S OWN URGENCY (item 7), directly under the call's. It
                changes nothing the system does -- no word in the broadcast, no
                move in the queue -- it is for the dispatcher's eyes. */}
            <div className="field">
              <label>Caller urgency — how urgently the caller needs help</label>
              <div className="chips">
                {PRIORITIES.map((p) => (
                  <button
                    type="button"
                    key={p.v}
                    className={`chip ${callerUrgency === p.v ? 'on' : ''}`}
                    onClick={() => setCallerUrgency(callerUrgency === p.v ? null : p.v)}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>

            {mayBroadcast && !past.on && (
              <div className="field">
                <label>Broadcast to</label>
                <AudiencePicker audiences={audiences} value={audience}
                                onChange={setAudience} disabled={busy} />
              </div>
            )}


            </section>
          </div>

          {/* THE CALL AS IT BUILDS, always on screen: nothing has to be
              scrolled back up to check, and "change" goes back to its step.
              The map and the exact message live here too, so the pin is in
              view while the caller is still on the phone. */}
          <aside className="iticket" aria-label="The call so far">
            {past.on && (
              <div className="tk-logged">
                <b>Logged afterwards.</b> Nothing will be sent to anybody — no broadcast,
                no texts, no emails.
              </div>
            )}
            <div className="tk">
              <div className="tk-h">The call so far</div>
              <div className="tk-nat">
                {code && <><span className="callcode">{code}</span> </>}
                {form.nature_label
                  ? allNatures.join(' + ')
                  : <span className="tk-none">No nature yet</span>}
              </div>
              <TicketLine label="Caller" go={() => goStep(1)}>
                {anon && <b className="tk-anon">Anonymous · </b>}
                {[form.caller_name.trim(), form.caller_phone.trim(), form.relationship_label]
                  .filter(Boolean).join(' · ') || <span className="tk-none">Not taken yet</span>}
                {form.caller_on_scene === false && <b className="tk-wait"> · not staying</b>}
              </TicketLine>
              {(notes.internal.trim() || notes.responder.trim()) && (
                <TicketLine label="Notes" go={() => goStep(1)}>
                  {notes.internal.trim() && <div className="tk-note internal">{notes.internal.trim()}</div>}
                  {notes.responder.trim() && <div className="tk-note responder">{notes.responder.trim()}</div>}
                </TicketLine>
              )}
              <TicketLine label="Where" go={() => goStep(2)}>
                {autoFilled && whereNow && (
                  <div className="tk-wait">From the caller's record — confirm it aloud</div>
                )}
                {whereNow
                  ? <>{form.location_label.trim() || details.trim()}
                      {form.location_label.trim() && details.trim() && <> · {details.trim()}</>}
                      {cross.a.trim() && cross.b.trim() && <> — between {cross.a.trim()} and {cross.b.trim()}</>}
                      {locNote.trim() && <div className="tk-sub">{locNote.trim()}</div>}</>
                  : <span className="tk-none">No address yet — the call needs one</span>}
              </TicketLine>
              <TicketLine label="Answers" go={() => goStep(3)}>
                {ticketAnswers.length
                  ? ticketAnswers.map(({ question, text }) => (
                      <div key={question.key}
                           className={alerting(question, answers[question.key] ?? '') ? 'tk-red' : ''}>
                        {question.prompt} <b>{text}</b>
                      </div>
                    ))
                  : <span className="tk-none">
                      {asked.length ? `${stillToAsk.length} to ask` : 'Nothing asked yet'}
                    </span>}
              </TicketLine>
              {past.on && (
                <>
                  <TicketLine label="Times" go={() => goStep(4)}>
                    {pastLine(past) || <span className="tk-none">When it came in — not yet</span>}
                  </TicketLine>
                  <TicketLine label="Who went" go={() => goStep(4)}>
                    {past.went.length
                      ? past.went.map((w) => `${w.who} (${w.role})`).join(' · ')
                      : <span className="tk-none">Nobody listed</span>}
                  </TicketLine>
                </>
              )}
              <TicketLine label={past.on ? 'Urgency' : 'Send'} go={() => goStep(4)}>
                {urgency !== null
                  ? PRIORITIES.find((p) => p.v === urgency)?.label
                  : raised ? <b className="tk-red">{ALERT_LABEL} — raised by an answer</b>
                    : <span className="tk-none">Urgency not chosen</span>}
                {area && !past.on && <> · area: {area}</>}
                {mayBroadcast && !past.on && <> · {audienceText}</>}
              </TicketLine>
            </div>
        {looking && <p className="maphint">Looking it up on the map…</p>}
        {pinned && (
          <>
            <PinMap
              latitude={pinned.lat}
              longitude={pinned.lon}
              zoom={pinned.wide ? 11 : 17}
              // A CLICK PLACES THE PIN only where nothing else would: a pin
              // put by hand, or one found from the details. On an address's
              // own map a stray click must not become a dispatcher's claim.
              clickToPlace={!pinned.address && step === 2}
              onMoved={(lat, lon) => setSpot((s) => (s ? { ...s, lat, lon, moved: true } : s))}
            />
            <p className="maphint">
              {pinned.moved
                ? <><strong>Pin placed by you.</strong> That is the spot the call carries.</>
                : pinned.wide
                  ? <>Drag the pin, or click the map, to where the caller is. Until you do, the call carries no pin — it goes either way.</>
                  : !pinned.address
                    // FOUND FROM THE DETAILS: nothing else will place this
                    // call, so it goes only once he confirms it.
                    ? <>Read it back to the caller, then click or drag the pin to confirm the spot. Until you do, the call carries no pin.</>
                    : <>Read it back to the caller, and drag the pin if the spot is different.</>}
            </p>
          </>
        )}
        {!pinned && mapSaid && <p className="maphint warn">{mapSaid}</p>}
        {/* THE MAP COULD NOT PLACE IT, OR WAS NEVER ASKED: the dispatcher
            puts the pin himself, from the service area (his Q2 A). */}
        {!pinned && !looking && whereNow && (
          <button type="button" className="ghost tiny" onClick={dropPin}>
            Put the pin on the map yourself
          </button>
        )}

          {/* THE MESSAGE ITSELF, header and footer included, before anybody is
              sent it. And the line under it says what goes to whoever TAKES it,
              because the text above is deliberately short of the house number. */}
          {mayBroadcast && willGet && !past.on && (
            <div className="willget">
              <h4>
                What your members will receive
                <span className={`n ${willGet.characters > 1600 ? 'over' : ''}`}>
                  {willGet.characters} characters
                  {willGet.characters > 1600 ? ' — the end will be cut off' : ''}
                </span>
              </h4>
              <pre>
                {willGet.header && <span className="wrap">{willGet.header}{'\n'}</span>}
                {willGet.body}
                {willGet.footer && <span className="wrap">{'\n'}{willGet.footer}</span>}
              </pre>
              <p className="after">
                Whoever you log as going also gets the full address
                {cross.a.trim() && cross.b.trim() ? ' and cross streets' : ''}, the
                caller's name and every answer
                {notes.responder.trim() ? ', and your notes for the responder' : ''}
                {' '}— never the callback number, and never the internal notes.
              </p>
            </div>
          )}


          </aside>
        </div>

        {/* THE BAR NEVER SCROLLS AWAY, AND "CREATE AND BROADCAST" IS ON EVERY
            STEP (his Q2 A): a child locked in a car goes out from step 2 or 3,
            without walking to step 4. */}
        {logging && timesWarn && (() => {
          const gaps = pastTimesMissing(past)
          const what = gaps.length === 2 ? 'The time it came in and the time it closed are'
            : gaps[0] === 'in' ? 'The time it came in is' : 'The time it closed is'
          return (
            <div className="lgc-warn" role="alertdialog" aria-label="Times not filled in">
              <div><b>{what} not filled in.</b> Save it anyway? It is kept on {past.date} and
                marked “times not known”. A time can be added later with Correct this call.</div>
              <div className="lgc-warn-btns">
                <button type="button" className="btn primary" disabled={busy}
                        onClick={() => saveLogged(true)}>Yes, save it</button>
                <button type="button" className="btn" onClick={() => setTimesWarn(false)}>No, go back</button>
              </div>
            </div>
          )
        })()}
        <div className="ibar">
          <button type="button" className={draftId ? 'btn dr-discard' : 'btn'} onClick={onCancel}>
            {draftId ? 'Discard draft' : 'Cancel'}
          </button>
          {/* SAVE AS DRAFT (074, his Q2 A): it is saved as he types; this
              closes the form and leaves it under Drafts, nothing sent. */}
          {onSaveDraft && (
            <button type="button" className="btn dr-save" disabled={busy} onClick={onSaveDraft}>
              Save as draft
            </button>
          )}
          {stillToAsk.length > 0 && (
            <span className="qwaiting">
              {stillToAsk.length} still unanswered{past.on ? '.' : ' — add them after the broadcast.'}
            </span>
          )}
          <span className="sep" />
          {missing && <span className="ineed">{missing}</span>}
          {step > 1 && (
            <button type="button" className="btn" onClick={() => goStep((step - 1) as Step)}>
              ← {stepNames[step - 2]}
            </button>
          )}
          {step < 4 && (
            <button type="button" className="btn ibar-next" onClick={() => goStep((step + 1) as Step)}>
              Next: {stepNames[step]} →
            </button>
          )}
          {/* NOT ONE QUESTION CAN DISABLE THIS. The two things that still can
              are the two the server itself refuses — a call with no nature and
              a call with no location — and a child locked in a car goes out in
              seconds whether or not anybody has said what kind of road it is. */}
          <button
            className="btn primary"
            // TRIMMED, because a box holding only spaces is an empty box:
            // it passed this test, passed the server's own `min_length`, and
            // opened a call whose broadcast named no address at all.
            disabled={busy || !form.nature_label.trim() || !whereNow
              // Nor can an audience -- EXCEPT one that provably reaches nobody,
              // which the server would refuse after the call had opened.
              || aimedAtNobody
              // A call that already happened: its times, and the tick (078).
              || pastLocked || !!pastNeeds}
          >
            {logging ? (busy ? 'Saving…' : 'Save the call')
              : busy ? 'Opening…' : mayBroadcast ? 'Create and broadcast' : 'Create the call'}
          </button>
        </div>
      </form>
    </div>
  )
}

/** One line of the ticket, with its way back to the step it came from. */
function TicketLine({ label, go, children }: { label: string; go: () => void; children: ReactNode }) {
  return (
    <div className="tk-line">
      <div className="tk-l">{label}
        <button type="button" className="tk-go" aria-label={`Change ${label.toLowerCase()}`} onClick={go}>change</button>
      </div>
      <div className="tk-v">{children}</div>
    </div>
  )
}
